/* eslint-disable */
import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import { useTable, useRowSelect } from 'react-table';
import { toast } from 'react-toastify';
import { Button, CardBody, Table, Modal, ModalHeader, ModalBody, ModalFooter, Label, Input } from 'reactstrap';
import { invoiceService } from '../../services/InvoiceService';
import { InvoicingDetailDto, InvoicingDto } from '../../types';

interface UnpaidInvoicesTableProps {
    invoices: InvoicingDetailDto[];
    onSave: () => void;
}

interface Props {
    indeterminate?: boolean;
    name: string;
}

interface EmailTemplateOption {
    value: number;
    label: string;
    description: string;
}

const useCombinedRefs = (...refs): React.MutableRefObject<any> => {
    const targetRef = React.useRef();

    React.useEffect(() => {
        refs.forEach((ref) => {
            if (!ref) return;

            if (typeof ref === 'function') {
                ref(targetRef.current);
            } else {
                ref.current = targetRef.current;
            }
        });
    }, [refs]);

    return targetRef;
};

const IndeterminateCheckbox = forwardRef<HTMLInputElement, Props>(
    ({ indeterminate, ...rest }, ref: React.Ref<HTMLInputElement>) => {
        const defaultRef = React.useRef(null);
        const combinedRef = useCombinedRefs(ref, defaultRef);

        useEffect(() => {
            if (combinedRef?.current) {
                combinedRef.current.indeterminate = indeterminate ?? false;
            }
        }, [combinedRef, indeterminate]);

        return (
            <React.Fragment>
                <input type="checkbox" ref={combinedRef} {...rest} />
            </React.Fragment>
        );
    },
);

const UnpaidInvoicesTable = (props: UnpaidInvoicesTableProps) => {
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [selectedTemplateType, setSelectedTemplateType] = useState<number>(2); // Default to FirstLatePaymentNotification

    const templateOptions: EmailTemplateOption[] = [
        {
            value: 1,
            label: 'Invoice Due',
            description: 'Standard invoice notification with payment details.',
        },
        {
            value: 2,
            label: '1st Late Payment Notification',
            description:
                'First reminder for overdue invoices. Sent to billing contact, program directors, CEO, and Dean.',
        },
        {
            value: 3,
            label: '2nd Late Payment Notification',
            description:
                'Second reminder for overdue invoices. Sent to billing contact, program directors, CEO, and Dean.',
        },
        {
            value: 4,
            label: 'Payment Not Received',
            description: 'Final notice for unpaid invoices with escalation information.',
        },
    ];

    const columns = useMemo(
        () => [
            { Header: '', accessor: 'id' },
            { Header: 'Invoice Number', accessor: 'invoiceNumber' },
            { Header: 'Institution', accessor: 'institutionTitle' },
            { Header: 'Recipient Name', accessor: (row) => `${row.recipient?.firstName} ${row.recipient?.lastName}` },
            { Header: 'Recipient Email', accessor: 'recipient?.email' },
            { Header: 'Amount Due', accessor: 'dueAmount' },
        ],
        [],
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        selectedFlatRows,
        state: { selectedRowIds },
    } = useTable(
        {
            columns,
            data: useMemo(() => [...props.invoices], [props.invoices]),
        },
        useRowSelect,
        (hooks) => {
            hooks.visibleColumns.push((columns) => [
                // Let's make a column for selection
                {
                    id: 'selection',
                    // The header can use the table's getToggleAllRowsSelectedProps method
                    // to render a checkbox
                    Header: ({ getToggleAllRowsSelectedProps }) => (
                        <div>
                            <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
                        </div>
                    ),
                    // The cell can use the individual row's getToggleRowSelectedProps method
                    // to the render a checkbox
                    Cell: ({ row }) => (
                        <div>
                            <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
                        </div>
                    ),
                },
                ...columns,
            ]);
        },
    );

    const openSendEmailModal = () => {
        if (selectedFlatRows.length === 0) {
            toast.warning('Please select at least one invoice to send.');
            return;
        }
        setIsModalOpen(true);
    };

    const handleSendEmails = () => {
        const toastId = toast.info('Sending invoices...', { autoClose: false });
        setIsModalOpen(false);

        invoiceService
            .emailInvoices(
                selectedFlatRows.map((x) => x.values.id),
                null,
                selectedTemplateType,
            )
            .then(() => {
                if (toast.isActive(toastId)) {
                    toast.update(toastId, {
                        type: 'success',
                        render: 'Invoices sent',
                        autoClose: 3000,
                        closeOnClick: true,
                        draggable: true,
                    });
                } else {
                    toast.success('Invoices sent');
                }
                props.onSave();
            })
            .catch(() => {
                if (toast.isActive(toastId)) {
                    toast.update(toastId, {
                        type: 'error',
                        render: 'Error sending invoices',
                        autoClose: 5000,
                        closeOnClick: true,
                        draggable: true,
                    });
                } else {
                    toast.error('Error sending invoices');
                }
            });
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };
    const setProbation = () => {
        const toastId = toast.info('Starting probation...', { autoClose: false });

        invoiceService
            .setProbation(selectedFlatRows.map((x) => x.values.id))
            .then(() => {
                if (toast.isActive(toastId)) {
                    toast.update(toastId, {
                        type: 'success',
                        render: 'Probation set',
                        autoClose: 3000,
                        closeOnClick: true,
                        draggable: true,
                    });
                } else {
                    toast.success('Probation set');
                }
                props.onSave();
            })
            .catch(() => {
                if (toast.isActive(toastId)) {
                    toast.update(toastId, {
                        type: 'error',
                        render: 'Error setting probation',
                        autoClose: 5000,
                        closeOnClick: true,
                        draggable: true,
                    });
                } else {
                    toast.error('Error setting probation');
                }
            });
    };

    return (
        <>
            <CardBody className="mb-3">
                <Button color="primary" onClick={openSendEmailModal}>
                    Send Selected Invoices
                </Button>
                <Button style={{ marginLeft: '8px' }} color="danger" onClick={() => setProbation()}>
                    Start Probation for Selected
                </Button>
            </CardBody>
            <Table {...getTableProps()}>
                <thead>
                    {headerGroups.map((headerGroup) => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column) => (
                                <th {...column.getHeaderProps()}>{column.render('Header')}</th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                    {rows.map((row, i) => {
                        prepareRow(row);
                        return (
                            <tr {...row.getRowProps()}>
                                {row.cells.map((cell) => {
                                    return <td {...cell.getCellProps()}>{cell.render('Cell')}</td>;
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </Table>

            {/* Send Invoices Modal */}
            <Modal isOpen={isModalOpen} toggle={handleCloseModal} size="lg">
                <ModalHeader toggle={handleCloseModal}>Send Invoice Emails</ModalHeader>
                <ModalBody>
                    <div style={{ marginBottom: '20px' }}>
                        <p>
                            <strong>Selected Invoices:</strong> {selectedFlatRows.length} invoice(s)
                        </p>
                        <p>Choose an email template for sending the selected invoices:</p>
                    </div>

                    <div>
                        <Label for="modalTemplateSelect" style={{ marginBottom: '10px', fontWeight: 'bold' }}>
                            Email Template:
                        </Label>
                        {templateOptions.map((option) => (
                            <div key={option.value} style={{ marginBottom: '15px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                                    <input
                                        type="radio"
                                        id={`template-${option.value}`}
                                        name="template"
                                        value={option.value}
                                        checked={selectedTemplateType === option.value}
                                        onChange={(e) => setSelectedTemplateType(Number(e.target.value))}
                                        style={{ marginRight: '8px' }}
                                    />
                                    <Label for={`template-${option.value}`} style={{ margin: 0, fontWeight: 'bold' }}>
                                        {option.label}
                                    </Label>
                                </div>
                                <div style={{ marginLeft: '24px', color: '#666', fontSize: '14px' }}>
                                    {option.description}
                                </div>
                            </div>
                        ))}
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={handleCloseModal}>
                        Cancel
                    </Button>
                    <Button color="primary" onClick={handleSendEmails}>
                        Send Emails
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default UnpaidInvoicesTable;
